import { GroupEntity, UserEntity } from '@backstage/catalog-model';

export function buildMemberCountMap(
  groups: GroupEntity[],
  users: UserEntity[],
): Map<string, number> {
  const countMap = new Map<string, number>();

  const parentOf = new Map<string, string>();
  for (const g of groups) {
    const parentRef = g.spec?.parent as string | undefined;
    const parentName = parentRef?.split('/').pop();
    if (parentName) parentOf.set(g.metadata.name, parentName);
  }

  // A member's direct groups come from two sources that don't always
  // agree: their own profile's memberOf, and each group's own spec.members
  // (populated when a member is added via the Team Editor, which never
  // writes to the member's own memberOf). Keyed by bare member name — not
  // by iterating `users` alone — so a spec.members entry is still counted
  // even if that user wasn't otherwise fetched.
  const directGroupsByMemberName = new Map<string, Set<string>>();
  const addDirectGroup = (memberName: string, groupName: string) => {
    if (!directGroupsByMemberName.has(memberName)) {
      directGroupsByMemberName.set(memberName, new Set());
    }
    directGroupsByMemberName.get(memberName)!.add(groupName);
  };

  for (const g of groups) {
    for (const memberRef of (g.spec?.members ?? []) as string[]) {
      const memberName = memberRef.split('/').pop();
      if (memberName) addDirectGroup(memberName, g.metadata.name);
    }
  }
  for (const u of users) {
    for (const groupRef of (u.spec?.memberOf ?? []) as string[]) {
      const groupName = groupRef.split('/').pop() ?? groupRef;
      addDirectGroup(u.metadata.name, groupName);
    }
  }

  for (const directGroups of directGroupsByMemberName.values()) {
    const visitedAncestors = new Set<string>(directGroups);
    for (const groupName of directGroups) {
      countMap.set(groupName, (countMap.get(groupName) ?? 0) + 1);
      let ancestor = parentOf.get(groupName);
      while (ancestor) {
        if (visitedAncestors.has(ancestor)) break;
        visitedAncestors.add(ancestor);
        countMap.set(ancestor, (countMap.get(ancestor) ?? 0) + 1);
        ancestor = parentOf.get(ancestor);
      }
    }
  }

  return countMap;
}
